import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * Translation key each configurable panel is titled with, reusing the very
 * labels the sidebar renders so both stay in step.
 */
export const SIDEBAR_ITEM_LABELS = Object.freeze({
  conversation_actions: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS',
  macros: 'CONVERSATION_SIDEBAR.ACCORDION.MACROS',
  conversation_info: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO',
  contact_attributes: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES',
  contact_notes: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES',
  shared_files: 'CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES',
  previous_conversation: 'CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION',
  conversation_participants: 'CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE',
  linear_issues: 'CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES',
  shopify_orders: 'CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS',
});

/**
 * Decides which conversation sidebar panels an agent may see, based on the
 * teams they belong to. Each team lists the panels it turns off; a panel is
 * hidden only when every team the agent belongs to turns it off, so belonging
 * to one team that keeps a panel is enough to keep seeing it.
 *
 * Agents outside any team see everything, which keeps accounts that do not use
 * teams working exactly as before.
 *
 * @returns {{hiddenSidebarItems: import('vue').ComputedRef<Set<String>>,
 *   filterSidebarItems: Function}}
 */
export function useTeamSidebarItems() {
  const myTeams = useMapGetter('teams/getMyTeams');

  const hiddenSidebarItems = computed(() => {
    const teams = myTeams.value ?? [];
    if (!teams.length) return new Set();

    const disabledPerTeam = teams.map(
      team => new Set(team.disabled_sidebar_items ?? [])
    );
    const [first, ...others] = disabledPerTeam;

    return new Set(
      [...first].filter(name => others.every(disabled => disabled.has(name)))
    );
  });

  const filterSidebarItems = items =>
    items.filter(item => !hiddenSidebarItems.value.has(item.name));

  return { hiddenSidebarItems, filterSidebarItems };
}
